import * as React from "react";
import { Eye, Heart, MessageCircle, Share2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { PostStatsEntry } from "@/services/bedrock/dtos/Stats";
import { kFormatter } from "@/utils/formatHelpers";

/** A post's counts; null when none of its networks report that metric. */
export interface PostCounts {
  impressions: number | null;
  reactions: number | null;
  comments: number | null;
  shares: number | null;
}

const sumField = (stats: PostStatsEntry[], field: keyof PostStatsEntry): number | null => {
  const reported = stats.map(s => s[field]).filter((v): v is number => typeof v === "number");
  return reported.length > 0 ? reported.reduce((sum, v) => sum + v, 0) : null;
};

/**
 * One post's counts across its networks: the fields bedrock totals, so they add up to the stats
 * pages. Null when no network has reported any of them yet, even if a stats sync has run.
 */
export function sumPostStats(stats: (PostStatsEntry | null | undefined)[]): PostCounts | null {
  const present = stats.filter((s): s is PostStatsEntry => !!s);
  const counts: PostCounts = {
    impressions: sumField(present, "impressionCount"),
    reactions: sumField(present, "totalReactions"),
    comments: sumField(present, "commentCount"),
    shares: sumField(present, "shareCount"),
  };
  return Object.values(counts).every(value => value == null) ? null : counts;
}

/** Unlike kFormatter, 0 reads "0" rather than "--". */
export function formatCount(n: number): string {
  if (n === 0) return "0";
  return n > 999 ? kFormatter(n) : String(n);
}

const METRICS = [
  { key: "impressions", label: "Impressions", Icon: Eye },
  { key: "reactions", label: "Reactions", Icon: Heart },
  { key: "comments", label: "Comments", Icon: MessageCircle },
  { key: "shares", label: "Shares", Icon: Share2 },
] as const;

export interface PostStatsRowProps {
  counts: PostCounts;
  className?: string;
}

/** Icon-and-count row for one post; metrics no network reports are left out, a reported 0 stays. */
export function PostStatsRow({ counts, className }: PostStatsRowProps): JSX.Element {
  return (
    <div
      className={cn("flex items-center gap-3 text-xs text-slate-600 tabular-nums", className)}
      data-testid="post-stats-row"
    >
      {METRICS.map(({ key, label, Icon }) => {
        const value = counts[key];
        if (value == null) return null;
        return (
          <span key={key} className="flex items-center gap-1" title={label}>
            <Icon className="size-3.5" aria-hidden="true" />
            <span className="sr-only">{label}</span>
            {formatCount(value)}
          </span>
        );
      })}
    </div>
  );
}
